"use client";
import { cva, type VariantProps } from "class-variance-authority";
import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
} from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../elements/button/button";
import { Image, type ImageProps } from "../../elements/image/image";
import { IconChevronRight } from "../../icons/chevron-right-icon";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { PoiImageHotspotInspectorProps } from "./poi-image-hotspot.preview";

const poiImageHotspotVariants = cva("", {
  variants: {
    background: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
    width: {
      full: "",
      contained: "",
    },
    hotspotColor: {
      blazeorange: "",
      darkteal: "",
      neonchartreuse: "",
    },
  },
  defaultVariants: {
    background: "white",
    width: "full",
    hotspotColor: "blazeorange",
  },
});

const hotspotMarkerClasses = {
  blazeorange: {
    active: "bg-blazeorange",
    ring: "border-blazeorange",
  },
  darkteal: {
    active: "bg-darkteal",
    ring: "border-darkteal",
  },
  neonchartreuse: {
    active: "bg-neonchartreuse",
    ring: "border-neonchartreuse",
  },
} as const;

export interface PoiImageHotspotItem {
  x: number;
  y: number;
  name: string;
  description?: string;
  link?: {
    href?: string;
    onClick?: (
      event: ReactMouseEvent<HTMLAnchorElement | HTMLButtonElement>
    ) => void;
    ariaLabel?: string;
  };
  thumbnailUrl?: string;
  thumbnailAlt?: string;
}

export interface PoiImageHotspotData {
  hotspots?: PoiImageHotspotItem[] | null;
}

export type PoiImageHotspotDetailsState = "visible" | "hidden";

export interface PoiImageHotspotDetailsActions {
  viewDetailsLabel: string;
  hideDetailsLabel: string;
  defaultState?: PoiImageHotspotDetailsState;
  onChange?: (
    state: PoiImageHotspotDetailsState,
    event: ReactMouseEvent<HTMLButtonElement>
  ) => void;
}

export interface PoiImageHotspotProps
  extends VariantProps<typeof poiImageHotspotVariants> {
  sys?: { id: string };
  internalName: string;
  imageUrl: string;
  imageAlt?: string;
  hotspots?: PoiImageHotspotData | null;
  aspectRatio?: ImageProps["aspectRatio"];
  defaultActiveHotspotIndex?: number;
  detailsActions?: PoiImageHotspotDetailsActions;
  background?: "white" | "whitesmoke" | "black";
  width?: "full" | "contained";
  hotspotColor?: "blazeorange" | "darkteal" | "neonchartreuse";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  className?: string;
  onHotspotChange?: (hotspot: PoiImageHotspotItem, index: number) => void;
  inspectorProps?: PoiImageHotspotInspectorProps;
}

interface HotspotCardPosition {
  left: number;
  top: number;
}

interface PoiImageHotspotState {
  clickedIndex: number | null;
  hoveredIndex: number | null;
  detailsState: PoiImageHotspotDetailsState;
  layoutVersion: number;
  hotspotCardPosition: HotspotCardPosition | null;
}

const PERCENTAGE = 100;
const FALLBACK_CARD_OFFSET_X = 4;
const FALLBACK_CARD_OFFSET_Y = 18;
const DEFAULT_ASPECT_RATIO = "16:9";
const EMPTY_HOTSPOTS: PoiImageHotspotItem[] = [];
const MOUSE_HOVER_MEDIA_QUERY = "(any-hover: hover) and (any-pointer: fine)";

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
}

function getInitialActiveHotspotIndex(
  hotspotCount: number,
  desiredIndex?: number
): number | null {
  if (hotspotCount === 0 || desiredIndex === undefined) {
    return null;
  }
  return clamp(desiredIndex, 0, hotspotCount - 1);
}

function supportsMouseHover(): boolean {
  return (
    typeof window.matchMedia !== "function" ||
    window.matchMedia(MOUSE_HOVER_MEDIA_QUERY).matches
  );
}

/**
 * Transforms a hotspot position from image-space (% of natural image dimensions)
 * to container-space (% of the fixed-aspect-ratio container) for objectFit="contain"
 *
 * Necessary because the Contentful editor records coordinates relative to the
 * natural image dimensions, while the container enforces a fixed aspect ratio
 */
function toContainerSpace(
  x: number,
  y: number,
  imageAR: number,
  containerAR: number
): { x: number; y: number } {
  if (imageAR >= containerAR) {
    // Image wider than container → letterbox top/bottom
    const renderedHeightPct = (containerAR / imageAR) * PERCENTAGE;
    const offsetY = (PERCENTAGE - renderedHeightPct) / 2;
    return { x, y: offsetY + (y / PERCENTAGE) * renderedHeightPct };
  }
  // Image taller than container → letterbox left/right
  const renderedWidthPct = (imageAR / containerAR) * PERCENTAGE;
  const offsetX = (PERCENTAGE - renderedWidthPct) / 2;
  return { x: offsetX + (x / PERCENTAGE) * renderedWidthPct, y };
}

export function PoiImageHotspot(
  props: Readonly<PoiImageHotspotProps>
): ReactNode {
  const hotspots = props.hotspots?.hotspots ?? EMPTY_HOTSPOTS;
  const normalizedHotspots = hotspots.map((hotspot) => ({
    ...hotspot,
    x: clamp(hotspot.x, 0, PERCENTAGE),
    y: clamp(hotspot.y, 0, PERCENTAGE),
  }));
  const hotspotCount = normalizedHotspots.length;

  const [imageNaturalAR, setImageNaturalAR] = useState<number | null>(null);

  const aspectRatio = props.aspectRatio ?? DEFAULT_ASPECT_RATIO;
  const [arW, arH] = aspectRatio.split(":").map(Number);
  const containerAR = arW / arH;
  const effectiveImageAR = imageNaturalAR ?? containerAR;

  const containerHotspots = normalizedHotspots.map((h) => ({
    ...h,
    ...toContainerSpace(h.x, h.y, effectiveImageAR, containerAR),
  }));

  const [state, setState] = useState<PoiImageHotspotState>(() => ({
    clickedIndex: getInitialActiveHotspotIndex(
      normalizedHotspots.length,
      props.defaultActiveHotspotIndex
    ),
    hoveredIndex: null,
    detailsState: props.detailsActions?.defaultState ?? "visible",
    layoutVersion: 0,
    hotspotCardPosition: null,
  }));
  const hotspotContainerRef = useRef<HTMLDivElement | null>(null);
  const hotspotCardRef = useRef<HTMLElement | null>(null);
  const deactivateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeHotspotIndex = state.hoveredIndex ?? state.clickedIndex;
  const activeHotspot =
    activeHotspotIndex !== null &&
    activeHotspotIndex >= 0 &&
    activeHotspotIndex < containerHotspots.length
      ? containerHotspots[activeHotspotIndex]
      : null;
  const activeHotspotX = activeHotspot?.x ?? null;
  const activeHotspotY = activeHotspot?.y ?? null;
  const areDetailsVisible = state.detailsState === "visible";
  const activeHotspotHasLink = Boolean(
    activeHotspot?.link?.href || activeHotspot?.link?.onClick
  );
  const activeHotspotCardStyle = activeHotspot
    ? ({
        "--poi-hotspot-card-left": state.hotspotCardPosition
          ? `${state.hotspotCardPosition.left}px`
          : `${clamp(activeHotspot.x + FALLBACK_CARD_OFFSET_X, 0, PERCENTAGE)}%`,
        "--poi-hotspot-card-top": state.hotspotCardPosition
          ? `${state.hotspotCardPosition.top}px`
          : `${clamp(activeHotspot.y - FALLBACK_CARD_OFFSET_Y, 0, PERCENTAGE)}%`,
      } as CSSProperties)
    : undefined;

  function updateHotspotCardPosition(nextPosition: HotspotCardPosition | null) {
    setState((previous) => {
      const previousPosition = previous.hotspotCardPosition;
      if (
        previousPosition?.left === nextPosition?.left &&
        previousPosition?.top === nextPosition?.top
      ) {
        return previous;
      }

      return {
        ...previous,
        hotspotCardPosition: nextPosition,
      };
    });
  }

  useEffect(
    () => () => {
      if (deactivateTimerRef.current) {
        clearTimeout(deactivateTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!props.imageUrl) {
      setImageNaturalAR(null);
      return;
    }

    let isMounted = true;
    const image = new window.Image();

    function updateNaturalAspectRatio() {
      if (!(isMounted && image.naturalWidth && image.naturalHeight)) {
        return;
      }
      setImageNaturalAR(image.naturalWidth / image.naturalHeight);
    }

    setImageNaturalAR(null);
    image.onload = updateNaturalAspectRatio;
    image.src = props.imageUrl;

    if (image.complete) {
      updateNaturalAspectRatio();
    }

    return () => {
      isMounted = false;
      image.onload = null;
    };
  }, [props.imageUrl]);

  useEffect(() => {
    setState((previous) => {
      if (
        previous.clickedIndex === null ||
        previous.clickedIndex < hotspotCount
      ) {
        return previous;
      }

      return {
        ...previous,
        clickedIndex: getInitialActiveHotspotIndex(
          hotspotCount,
          props.defaultActiveHotspotIndex
        ),
        hoveredIndex: null,
        hotspotCardPosition: null,
      };
    });
  }, [hotspotCount, props.defaultActiveHotspotIndex]);

  const hotspotColor = props.hotspotColor ?? "blazeorange";
  const markerClasses = hotspotMarkerClasses[hotspotColor];
  const isContentFullWidth = props.width !== "contained";
  const columnClasses = isContentFullWidth
    ? "col-span-12"
    : "col-span-10 col-start-2";

  useEffect(() => {
    if (!areDetailsVisible || typeof ResizeObserver === "undefined") {
      return;
    }

    const containerElement = hotspotContainerRef.current;
    if (!containerElement) {
      return;
    }

    const observer = new ResizeObserver(() => {
      setState((previous) => ({
        ...previous,
        layoutVersion: previous.layoutVersion + 1,
      }));
    });
    observer.observe(containerElement);

    return () => observer.disconnect();
  }, [areDetailsVisible]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: layoutVersion is intentionally used as a re-run trigger when the container resizes
  useLayoutEffect(() => {
    if (!(areDetailsVisible && activeHotspot)) {
      updateHotspotCardPosition(null);
      return;
    }

    const containerElement = hotspotContainerRef.current;
    const cardElement = hotspotCardRef.current;
    if (!(containerElement && cardElement)) {
      return;
    }

    const containerRect = containerElement.getBoundingClientRect();
    const cardRect = cardElement.getBoundingClientRect();

    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const cardWidth = cardRect.width;
    const cardHeight = cardRect.height;

    if (!(containerWidth && containerHeight && cardWidth && cardHeight)) {
      updateHotspotCardPosition(null);
      return;
    }

    const edgePadding = 16;
    const markerGap = 24;

    const markerX = (activeHotspot.x / PERCENTAGE) * containerWidth;
    const markerY = (activeHotspot.y / PERCENTAGE) * containerHeight;

    const rightLeft = markerX + markerGap;
    const leftLeft = markerX - markerGap - cardWidth;
    const fitsRight = rightLeft + cardWidth <= containerWidth - edgePadding;
    const fitsLeft = leftLeft >= edgePadding;

    let left = markerX <= containerWidth / 2 ? rightLeft : leftLeft;
    if (markerX <= containerWidth / 2 && !fitsRight && fitsLeft) {
      left = leftLeft;
    }
    if (markerX > containerWidth / 2 && !fitsLeft && fitsRight) {
      left = rightLeft;
    }
    const maxLeft = Math.max(
      edgePadding,
      containerWidth - cardWidth - edgePadding
    );
    left = clamp(left, edgePadding, maxLeft);

    let top = markerY - cardHeight / 2;
    const maxTop = Math.max(
      edgePadding,
      containerHeight - cardHeight - edgePadding
    );
    top = clamp(top, edgePadding, maxTop);

    updateHotspotCardPosition({ left, top });
  }, [
    activeHotspotIndex,
    activeHotspotX,
    activeHotspotY,
    areDetailsVisible,
    state.layoutVersion,
    effectiveImageAR,
    containerAR,
  ]);

  // Close the clicked hotspot card after an intentional outside click/tap.
  // Touch scrolling does not emit a click, so an open mobile card stays visible
  // while the user scrolls through the page.
  useEffect(() => {
    if (state.clickedIndex === null) {
      return;
    }

    function handleDocumentClick(event: globalThis.MouseEvent) {
      const clickedInsideHotspot = event
        .composedPath()
        .some(
          (target) =>
            target instanceof Element &&
            (target.matches('[data-testid="poi-hotspot-detail-card"]') ||
              target.matches('[data-testid^="poi-hotspot-marker-"]'))
        );
      if (clickedInsideHotspot) {
        return;
      }
      setState((previous) =>
        previous.clickedIndex === null
          ? previous
          : { ...previous, clickedIndex: null }
      );
    }

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [state.clickedIndex]);

  function cancelDeactivate() {
    if (deactivateTimerRef.current) {
      clearTimeout(deactivateTimerRef.current);
      deactivateTimerRef.current = null;
    }
  }

  function handleHoverPointerEnter(event: ReactPointerEvent<HTMLElement>) {
    if (event.pointerType === "mouse" && supportsMouseHover()) {
      cancelDeactivate();
    }
  }

  const DEACTIVATE_DELAY_MS = 150;

  function scheduleDeactivate(event: ReactPointerEvent<HTMLElement>) {
    if (event.pointerType !== "mouse") {
      return;
    }

    deactivateTimerRef.current = setTimeout(() => {
      setState((previous) => ({
        ...previous,
        hoveredIndex: null,
      }));
    }, DEACTIVATE_DELAY_MS);
  }

  function handleHotspotPointerEnter(
    index: number,
    event: ReactPointerEvent<HTMLButtonElement>
  ) {
    if (event.pointerType !== "mouse" || !supportsMouseHover()) {
      return;
    }

    cancelDeactivate();
    setState((previous) => ({
      ...previous,
      clickedIndex:
        previous.clickedIndex === index ? previous.clickedIndex : null,
      hoveredIndex: index,
    }));
  }

  function handleHotspotClick(index: number) {
    cancelDeactivate();
    setState((previous) => ({
      ...previous,
      clickedIndex: index,
      hoveredIndex: null,
    }));
    props.onHotspotChange?.(normalizedHotspots[index], index);
  }

  function handleDetailsStateChange(
    nextState: PoiImageHotspotDetailsState,
    event: ReactMouseEvent<HTMLButtonElement>
  ) {
    setState((previous) => ({
      ...previous,
      detailsState: nextState,
    }));
    props.detailsActions?.onChange?.(nextState, event);
  }

  return (
    <Container
      className={cn(
        poiImageHotspotVariants({
          background: props.background,
          width: props.width,
          hotspotColor: props.hotspotColor,
        }),
        "component-poi-image-hotspot",
        props.className
      )}
      fullWidth={true}
      outerContainerOnly={isContentFullWidth}
      paddingBottom={props.paddingBottom ?? "none"}
      paddingTop={props.paddingTop ?? "none"}
      testId="poi-image-hotspot"
      {...props.inspectorProps?.container}
    >
      <Row>
        <Column className={columnClasses}>
          <div className="relative">
            <div
              className="relative"
              style={{
                aspectRatio: aspectRatio.replace(":", " / "),
              }}
            >
              <div className="absolute inset-0 overflow-hidden rounded-sm">
                <Image
                  alt={props.imageAlt ?? props.internalName}
                  aspectRatio={aspectRatio}
                  className="h-full w-full"
                  fill
                  loading="lazy"
                  objectFit="contain"
                  src={props.imageUrl}
                />
              </div>

              <div
                className="absolute inset-0 z-10 overflow-visible"
                ref={hotspotContainerRef}
              >
                {areDetailsVisible &&
                  containerHotspots.map((hotspot, index) => {
                    const isActive = activeHotspotIndex === index;

                    return (
                      <button
                        aria-label={`Open hotspot: ${hotspot.name}`}
                        aria-pressed={isActive}
                        className={cn(
                          "-translate-x-1/2 -translate-y-1/2 absolute z-20 flex h-[39px] w-[39px] items-center justify-center rounded-full bg-transparent",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/35 focus-visible:ring-offset-2"
                        )}
                        data-testid={`poi-hotspot-marker-${index}`}
                        key={`${hotspot.name}-${index}`}
                        onClick={() => handleHotspotClick(index)}
                        onPointerEnter={(event) =>
                          handleHotspotPointerEnter(index, event)
                        }
                        onPointerLeave={scheduleDeactivate}
                        style={{
                          left: `${hotspot.x}%`,
                          top: `${hotspot.y}%`,
                        }}
                        type="button"
                      >
                        {isActive ? (
                          <span
                            aria-hidden="true"
                            className={cn(
                              "h-[21px] w-[21px] rounded-full",
                              markerClasses.active
                            )}
                          />
                        ) : (
                          <span
                            aria-hidden="true"
                            className="relative flex h-[21px] w-[21px] items-center justify-center"
                          >
                            <span
                              className={cn(
                                "absolute inset-0 animate-poi-hotspot-pulse rounded-full border-[4.5px] bg-transparent",
                                markerClasses.ring
                              )}
                            />
                            <span
                              className={cn(
                                "relative h-full w-full rounded-full border-[4.5px] bg-transparent",
                                markerClasses.ring
                              )}
                            />
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {areDetailsVisible &&
              activeHotspot &&
              activeHotspotIndex !== null && (
                <div
                  className="relative z-30 mt-3 animate-poi-card-in px-4 md:absolute md:top-[var(--poi-hotspot-card-top)] md:left-[var(--poi-hotspot-card-left)] md:mt-0 md:px-0"
                  data-testid="poi-hotspot-detail-card"
                  key={`card-${activeHotspotIndex}`}
                  onPointerEnter={handleHoverPointerEnter}
                  onPointerLeave={scheduleDeactivate}
                  style={activeHotspotCardStyle}
                >
                  <article
                    className="overflow-hidden rounded-[4px] bg-white p-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.02)] md:min-h-[112px]"
                    ref={hotspotCardRef}
                  >
                    <div className="flex items-start gap-[12px] md:items-center md:gap-[16px]">
                      {activeHotspot.thumbnailUrl && (
                        <div className="relative h-[64px] w-[64px] shrink-0 overflow-hidden bg-alabastergrey md:h-[80px] md:w-[80px]">
                          <Image
                            alt={
                              activeHotspot.thumbnailAlt ??
                              `Hotspot preview for ${activeHotspot.name}`
                            }
                            aspectRatio="1:1"
                            className="h-full w-full"
                            fill
                            loading="lazy"
                            objectFit="cover"
                            src={activeHotspot.thumbnailUrl}
                          />
                        </div>
                      )}
                      <div className="flex min-w-0 flex-1 flex-col gap-[8px] md:w-[140px] md:flex-none md:shrink-0">
                        <p className="font-bold font-brand text-[13px] text-black leading-[16px] tracking-[0.24px] md:text-[12px] md:leading-[14px]">
                          {activeHotspot.name}
                        </p>
                        {activeHotspot.description && (
                          <p className="font-brand text-[12px] text-black leading-[16px] md:text-[10px] md:leading-[13px]">
                            {activeHotspot.description}
                          </p>
                        )}
                      </div>

                      {activeHotspotHasLink && (
                        <Button
                          aria-label={
                            activeHotspot?.link?.ariaLabel ??
                            `Open link for hotspot ${activeHotspot.name}`
                          }
                          className="size-12 shrink-0"
                          href={activeHotspot?.link?.href}
                          mode="D2C"
                          onClick={activeHotspot?.link?.onClick}
                          size="icon"
                          variant="outline"
                        >
                          <IconChevronRight />
                        </Button>
                      )}
                    </div>
                  </article>
                </div>
              )}
          </div>

          {props.detailsActions && (
            <div className="mt-8 flex justify-center">
              <div
                className="inline-flex items-center rounded-[8px] border border-white/70 bg-black/85 p-1"
                data-testid="poi-hotspot-details-toggle"
              >
                <button
                  aria-label={props.detailsActions.viewDetailsLabel}
                  aria-pressed={areDetailsVisible}
                  className={cn(
                    "w-[120px] rounded-[6px] px-4 py-[16px] font-brand text-[12px] leading-[17px] transition",
                    areDetailsVisible
                      ? "bg-alabastergrey text-black"
                      : "bg-transparent text-white hover:bg-white/10"
                  )}
                  onClick={(event) =>
                    handleDetailsStateChange("visible", event)
                  }
                  type="button"
                >
                  {props.detailsActions.viewDetailsLabel}
                </button>
                <button
                  aria-label={props.detailsActions.hideDetailsLabel}
                  aria-pressed={!areDetailsVisible}
                  className={cn(
                    "w-[120px] rounded-[6px] px-4 py-[16px] font-brand text-[12px] leading-[17px] transition",
                    areDetailsVisible
                      ? "bg-transparent text-white hover:bg-white/10"
                      : "bg-alabastergrey text-black"
                  )}
                  onClick={(event) => handleDetailsStateChange("hidden", event)}
                  type="button"
                >
                  {props.detailsActions.hideDetailsLabel}
                </button>
              </div>
            </div>
          )}
        </Column>
      </Row>
    </Container>
  );
}
